package com.wintermar.inspection.di

import com.wintermar.inspection.core.data.AppSetting
import com.wintermar.inspection.feature.domain.repository.AuthRepository
import com.wintermar.inspection.feature.domain.repository.InspectionRepository
import com.wintermar.inspection.feature.domain.repository.ReferenceRepository
import com.wintermar.inspection.feature.domain.repository.TemplateRepository
import com.wintermar.inspection.feature.domain.repository.answer.AnswerChunkFileRepository
import com.wintermar.inspection.feature.domain.repository.answer.AnswerFileRepository
import com.wintermar.inspection.feature.domain.repository.answer.AnswerLocationRepository
import com.wintermar.inspection.feature.domain.repository.answer.AnswerRepository
import com.wintermar.inspection.feature.domain.repository.answer.AnswerSelectionRepository
import com.wintermar.inspection.feature.domain.usecase.answer.CheckGenerateChunkUseCase
import com.wintermar.inspection.feature.domain.usecase.answer.GetPendingUploadFilesUseCase
import com.wintermar.inspection.feature.domain.usecase.answer.SubmitAnswerUseCase
import com.wintermar.inspection.feature.domain.usecase.answer.UpdateHasGenerateChunkUseCase
import com.wintermar.inspection.feature.domain.usecase.answer.UpdateUploadStatusUseCase
import com.wintermar.inspection.feature.domain.usecase.answer.UploadChunkFileUseCase
import com.wintermar.inspection.feature.domain.usecase.answer.UpsertAnswerUseCase
import com.wintermar.inspection.feature.domain.usecase.auth.GetAuthStatusUseCase
import com.wintermar.inspection.feature.domain.usecase.auth.GetAuthStatusUseCaseImpl
import com.wintermar.inspection.feature.domain.usecase.auth.GetUserUseCase
import com.wintermar.inspection.feature.domain.usecase.auth.GetUserUseCaseImpl
import com.wintermar.inspection.feature.domain.usecase.auth.LoginUseCase
import com.wintermar.inspection.feature.domain.usecase.auth.LoginUseCaseImpl
import com.wintermar.inspection.feature.domain.usecase.inspection.ApproveInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.CompleteInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.CreateNewInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.DeleteInspectionReportUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.DeleteInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.DeleteInspectionUseCaseImpl
import com.wintermar.inspection.feature.domain.usecase.inspection.DownloadInspectionReportUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.GetInspectionDetailUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.GetInspectionDetailUseCaseImpl
import com.wintermar.inspection.feature.domain.usecase.inspection.GetListInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.GetListInspectionUseCaseImpl
import com.wintermar.inspection.feature.domain.usecase.inspection.StartInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.SyncInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.UpdateProgressInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetListTemplateOptionUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetListTemplateOptionUseCaseImpl
import com.wintermar.inspection.feature.domain.usecase.reference.GetListVesselUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetListVesselUseCaseImpl
import com.wintermar.inspection.feature.domain.usecase.reference.GetProfileUseCase
import com.wintermar.inspection.feature.domain.usecase.template.CollectTemplateUseCase
import com.wintermar.inspection.feature.domain.usecase.template.CollectTemplateUseCaseImpl
import com.wintermar.inspection.feature.domain.usecase.template.GetListTemplateUseCase
import com.wintermar.inspection.feature.domain.usecase.template.GetListTemplateUseCaseImpl
import com.wintermar.inspection.feature.domain.usecase.template.GetSimpleListTemplateUseCase
import com.wintermar.inspection.feature.domain.usecase.template.GetSimpleListTemplateUseCaseImpl
import com.wintermar.inspection.feature.domain.usecase.template.GetTemplateByIdUseCase
import com.wintermar.inspection.feature.domain.usecase.template.GetTemplateByIdUseCaseImpl
import org.koin.dsl.module

val useCaseModule = module {
    single<LoginUseCase> {
        LoginUseCaseImpl(
            authRepository = get<AuthRepository>(),
            appSetting = get<AppSetting>()
        )
    }

    single<GetAuthStatusUseCase> {
        GetAuthStatusUseCaseImpl(
            appSetting = get<AppSetting>()
        )
    }

    single<GetListTemplateUseCase> {
        GetListTemplateUseCaseImpl(
            templateRepository = get<TemplateRepository>()
        )
    }

    single<GetTemplateByIdUseCase> {
        GetTemplateByIdUseCaseImpl(
            templateRepository = get<TemplateRepository>()
        )
    }

    single<GetListTemplateOptionUseCase> {
        GetListTemplateOptionUseCaseImpl(
            referenceRepository = get<ReferenceRepository>()
        )
    }

    single<CollectTemplateUseCase> {
        CollectTemplateUseCaseImpl(
            templateRepository = get<TemplateRepository>()
        )
    }

    single<GetUserUseCase> {
        GetUserUseCaseImpl(
            appSetting = get<AppSetting>()
        )
    }

    single<UpsertAnswerUseCase> {
        UpsertAnswerUseCase(
            answerRepository = get<AnswerRepository>(),
            answerLocationRepository = get<AnswerLocationRepository>(),
            answerSelectionRepository = get<AnswerSelectionRepository>(),
            answerChunkFileRepository = get<AnswerChunkFileRepository>(),
            answerFileRepository = get<AnswerFileRepository>()
        )
    }

    single<GetListVesselUseCase> {
        GetListVesselUseCaseImpl(
            referenceRepository = get<ReferenceRepository>(),
        )
    }

    single<GetSimpleListTemplateUseCase> {
        GetSimpleListTemplateUseCaseImpl(
            templateRepository = get<TemplateRepository>()
        )
    }

    single<CreateNewInspectionUseCase> {
        CreateNewInspectionUseCase(
            templateRepository = get<TemplateRepository>(),
            inspectionRepository = get<InspectionRepository>(),
            answerRepository = get<AnswerRepository>()
        )
    }

    single<GetInspectionDetailUseCase> {
        GetInspectionDetailUseCaseImpl(
            inspectionRepository = get<InspectionRepository>(),
            answerRepository = get<AnswerRepository>(),
            templateRepository = get<TemplateRepository>(),
            appSetting = get<AppSetting>()
        )
    }

    single<GetListInspectionUseCase> {
        GetListInspectionUseCaseImpl(
            inspectionRepository = get<InspectionRepository>()
        )
    }

    single<StartInspectionUseCase> {
        StartInspectionUseCase(
            inspectionRepository = get<InspectionRepository>()
        )
    }

    single<UpdateProgressInspectionUseCase> {
        UpdateProgressInspectionUseCase(
            inspectionRepository = get<InspectionRepository>()
        )
    }

    single<SubmitAnswerUseCase> {
        SubmitAnswerUseCase(
            answerRepository = get<AnswerRepository>()
        )
    }

    single<UploadChunkFileUseCase> {
        UploadChunkFileUseCase(
            answerChunkFileRepository = get<AnswerChunkFileRepository>()
        )
    }

    single<UpdateUploadStatusUseCase> {
        UpdateUploadStatusUseCase(
            answerFileRepository = get<AnswerFileRepository>()
        )
    }

    single<CompleteInspectionUseCase> {
        CompleteInspectionUseCase(
            inspectionRepository = get<InspectionRepository>()
        )
    }

    single<GetPendingUploadFilesUseCase> {
        GetPendingUploadFilesUseCase(
            answerRepository = get<AnswerRepository>()
        )
    }

    single<DeleteInspectionUseCase> {
        DeleteInspectionUseCaseImpl(
            inspectionRepository = get<InspectionRepository>(),
            templateRepository = get<TemplateRepository>(),
            answerRepository = get<AnswerRepository>(),
            answerFileRepository = get<AnswerFileRepository>(),
            answerLocationRepository = get<AnswerLocationRepository>(),
            answerSelectionRepository = get<AnswerSelectionRepository>()
        )
    }

    single<ApproveInspectionUseCase> {
        ApproveInspectionUseCase(
            inspectionRepository = get<InspectionRepository>()
        )
    }

    single<GetProfileUseCase> {
        GetProfileUseCase(
            referenceRepository = get<ReferenceRepository>(),
            appSetting = get<AppSetting>()
        )
    }

    single<SyncInspectionUseCase> {
        SyncInspectionUseCase(
            inspectionRepository = get<InspectionRepository>()
        )
    }

    single<DownloadInspectionReportUseCase> {
        DownloadInspectionReportUseCase(
            inspectionRepository = get<InspectionRepository>()
        )
    }

    single<DeleteInspectionReportUseCase> {
        DeleteInspectionReportUseCase(
            inspectionRepository = get<InspectionRepository>()
        )
    }

    single<UpdateHasGenerateChunkUseCase> {
        UpdateHasGenerateChunkUseCase(
            answerFileRepository = get<AnswerFileRepository>()
        )
    }

    single<CheckGenerateChunkUseCase> {
        CheckGenerateChunkUseCase(
            answerFileRepository = get<AnswerFileRepository>()
        )
    }
}
